import React, { useState, useRef, useCallback } from 'react';
import { Camera, RefreshCw, Check, X, Sparkles, Upload, User } from 'lucide-react';
import api from '../utils/api';
import { useToast } from '../hooks/use-toast';

const AvatarCreation = ({ onAvatarCreated, onSkip, isRequired = true }) => {
  const [step, setStep] = useState('capture'); // capture, preview, generating, complete
  const [capturedImage, setCapturedImage] = useState(null);
  const [generatedAvatar, setGeneratedAvatar] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const streamRef = useRef(null);
  const { toast } = useToast();

  // Start camera
  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user', width: 640, height: 480 } 
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      setError('Could not access camera. Please allow camera permissions or upload a photo.');
      // console.error('Camera error:', err);
    }
  }, []);

  // Stop camera
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  }, []);

  // Capture photo from camera
  const capturePhoto = useCallback(() => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      const imageData = canvas.toDataURL('image/jpeg', 0.8);
      setCapturedImage(imageData);
      setStep('preview');
      stopCamera();
    }
  }, [stopCamera]);

  // Handle file upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCapturedImage(reader.result);
        setStep('preview');
        stopCamera();
      };
      reader.readAsDataURL(file);
    }
  };

  // Enhance photo using AI
  const generateAvatar = async () => {
    if (!capturedImage) return;
    
    setIsGenerating(true);
    setStep('generating');
    setError(null);

    try {
      const response = await api.post('/avatar/generate', {
        image_base64: capturedImage
      });

      if (response.data?.avatar_url) {
        setGeneratedAvatar(response.data.avatar_url);
        setStep('complete');
        toast({
          title: "Photo Enhanced!",
          description: "Your professional profile photo is ready!",
        });
      } else {
        throw new Error('No enhanced photo returned');
      }
    } catch (err) {
      // console.error('Photo enhancement error:', err);
      setError('Could not enhance photo. Please try again.');
      setStep('preview');
      toast({
        title: "Enhancement Failed",
        description: err.response?.data?.detail || "Please try again",
        variant: "destructive"
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Retry with new photo
  const retryCapture = () => {
    setCapturedImage(null);
    setGeneratedAvatar(null);
    setError(null);
    setStep('capture');
    startCamera();
  };

  // Complete the avatar creation - photo is already saved to DB by the API
  const completeAvatarCreation = () => {
    if (onAvatarCreated && generatedAvatar) {
      // The photo is already saved in the database by the /avatar/generate endpoint
      // Just notify parent component to refresh user data
      onAvatarCreated(generatedAvatar);
    }
  };

  // Start camera when component mounts
  React.useEffect(() => {
    if (step === 'capture') {
      startCamera();
    }
    return () => stopCamera();
  }, [step, startCamera, stopCamera]);

  return (
    <div className="min-h-screen heavenly-background flex items-center justify-center p-4">
      {/* Floating cloud particles */}
      <div className="heavenly-clouds">
        <span className="cloud-particle">☁️</span>
        <span className="cloud-particle">☁️</span>
        <span className="cloud-particle">☁️</span>
        <span className="cloud-particle">☁️</span>
      </div>
      
      <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden relative z-10">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary to-cyan-500 text-white p-6 text-center">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <User className="w-8 h-8" />
          </div>
          <h2 className="text-2xl font-bold font-outfit">Create Your Profile Photo</h2>
          <p className="text-white/90 mt-2">
            {step === 'capture' && "Take a photo - AI will enhance it to look professional!"}
            {step === 'preview' && "Looking good! Ready to enhance your photo?"}
            {step === 'generating' && "AI is enhancing your photo..."}
            {step === 'complete' && "Your professional photo is ready!"}
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step 1: Capture */}
          {step === 'capture' && (
            <div className="space-y-6">
              <div className="relative aspect-square bg-black rounded-2xl overflow-hidden">
                <video 
                  ref={videoRef} 
                  autoPlay 
                  playsInline 
                  muted
                  className="w-full h-full object-cover"
                />
                {error && (
                  <div className="absolute inset-0 bg-gray-900 flex items-center justify-center text-white text-center p-4">
                    <div>
                      <Camera className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>{error}</p>
                    </div>
                  </div>
                )}
                <canvas ref={canvasRef} className="hidden" />
              </div>

              <div className="flex gap-4">
                <button
                  onClick={capturePhoto}
                  className="flex-1 py-4 bg-gradient-to-r from-primary to-cyan-500 text-white rounded-xl font-bold text-lg hover:shadow-lg transition-all flex items-center justify-center gap-2"
                >
                  <Camera className="w-6 h-6" />
                  Take Photo
                </button>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="px-6 py-4 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium transition-all"
                >
                  <Upload className="w-6 h-6" />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </div>

              <p className="text-center text-sm text-gray-600">
                Position your face in the center and smile! 😊
              </p>
            </div>
          )}

          {/* Step 2: Preview */}
          {step === 'preview' && (
            <div className="space-y-6">
              <div className="aspect-square bg-gray-100 rounded-2xl overflow-hidden">
                <img 
                  src={capturedImage} 
                  alt="Captured"
                  className="w-full h-full object-cover"
                />
              </div>

              {error && (
                <div className="p-4 bg-red-50 text-red-700 rounded-xl text-center">
                  {error}
                </div>
              )}

              <div className="flex gap-4">
                <button
                  onClick={retryCapture}
                  className="flex-1 py-4 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium transition-all flex items-center justify-center gap-2"
                >
                  <RefreshCw className="w-5 h-5" />
                  Retake
                </button>
                <button
                  onClick={generateAvatar}
                  className="flex-1 py-4 bg-gradient-to-r from-primary to-cyan-500 text-white rounded-xl font-bold text-lg hover:shadow-lg transition-all flex items-center justify-center gap-2"
                >
                  <Sparkles className="w-5 h-5" />
                  Enhance Photo
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Generating */}
          {step === 'generating' && (
            <div className="text-center py-12">
              <div className="relative w-32 h-32 mx-auto mb-6">
                <div className="absolute inset-0 rounded-full border-4 border-primary/20"></div>
                <div className="absolute inset-0 rounded-full border-4 border-t-primary animate-spin"></div>
                <div className="absolute inset-4 bg-gradient-to-br from-primary/10 to-cyan-100 rounded-full flex items-center justify-center">
                  <Sparkles className="w-10 h-10 text-primary animate-pulse" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Enhancing Your Photo</h3>
              <p className="text-gray-600">AI is creating your professional headshot...</p>
              <div className="mt-4 flex justify-center gap-1">
                {[0, 1, 2].map((i) => (
                  <div 
                    key={i}
                    className="w-3 h-3 bg-primary rounded-full animate-bounce"
                    style={{ animationDelay: `${i * 0.2}s` }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Step 4: Complete */}
          {step === 'complete' && generatedAvatar && (
            <div className="space-y-6">
              <div className="flex gap-6 items-center justify-center">
                {/* Original Photo */}
                <div className="text-center">
                  <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-gray-200">
                    <img src={capturedImage} alt="Original" className="w-full h-full object-cover" />
                  </div>
                  <p className="text-sm text-gray-600 mt-2">Original</p>
                </div>

                <div className="text-3xl text-gray-400">→</div>

                {/* Enhanced Photo */}
                <div className="text-center">
                  <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-primary shadow-lg animate-bounce-in">
                    <img src={generatedAvatar} alt="Enhanced" className="w-full h-full object-cover" />
                  </div>
                  <p className="text-sm text-primary font-medium mt-2">Enhanced!</p>
                </div>
              </div>

              <div className="text-center p-4 bg-green-50 rounded-xl">
                <Check className="w-8 h-8 text-green-500 mx-auto mb-2" />
                <p className="text-green-700 font-medium">Professional photo created!</p>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={retryCapture}
                  className="flex-1 py-4 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium transition-all flex items-center justify-center gap-2"
                  data-testid="avatar-retry-btn"
                >
                  <RefreshCw className="w-5 h-5" />
                  Try Again
                </button>
                <button
                  onClick={completeAvatarCreation}
                  className="flex-1 py-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-bold text-lg hover:shadow-lg transition-all flex items-center justify-center gap-2"
                  data-testid="avatar-use-btn"
                >
                  <Check className="w-5 h-5" />
                  Use This Photo
                </button>
              </div>
            </div>
          )}

          {/* Skip option (if not required) */}
          {!isRequired && step !== 'generating' && step !== 'complete' && (
            <button
              onClick={onSkip}
              className="w-full mt-4 py-2 text-gray-600 hover:text-gray-800 text-sm"
            >
              Skip for now
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AvatarCreation;
